// src/lib/api/automation/rules.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getCurrentUser } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'

/**
 * Get automations with actions
 * Migrated from supabaseApi.js lines 138-188
 * @param {string} [providedCompanyId] - Optional company ID to use (avoids internal lookup)
 */
const _getAutomationsWithActions = async (providedCompanyId = null) => {
  console.log('[automation] getAutomationsWithActions called', providedCompanyId ? 'with provided companyId' : 'will fetch companyId')
  
  // Use provided companyId or fetch it
  const companyId = providedCompanyId || await getValidatedCompanyId()
  if (!companyId) {
    console.warn('[automation] No company ID found')
    return createSuccessResponse([])
  }
  console.log('[automation] Using company ID:', companyId)

  // Select all columns - handle missing columns gracefully
  // Use select('*') to get all columns - handles missing columns gracefully
  const { data: automations, error: automationsError } = await supabase
    .from('automations')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })

  if (automationsError) {
    console.error('[automation] Error fetching automations:', automationsError)
    // If error is about missing columns, try without the new columns
    if (automationsError.message && automationsError.message.includes('column') && automationsError.message.includes('does not exist')) {
      console.warn('[automation] Missing columns detected, retrying with basic columns only')
      // Retry with just basic columns (status exists, is_active does not)
      const { data: retryData, error: retryError } = await supabase
        .from('automations')
        .select('id, name, trigger_type, trigger_config, status, company_id, created_at, updated_at')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      
      if (retryError) {
        console.error('[automation] Retry also failed:', retryError)
        throw retryError
      }
      
      // Map retry data to include nulls for missing columns
      return createSuccessResponse((retryData || []).map(a => ({
        ...a,
        category: null,
        time_offset_minutes: null,
        time_offset_type: null,
        actions: []
      })))
    }
    throw automationsError
  }

  if (!automations || automations.length === 0) {
    console.log('[automation] No automations found')
    return createSuccessResponse([])
  }

  const automationIds = automations.map(a => a.id)
  const { data: actions, error: actionsError } = await supabase
    .from('automation_actions')
    .select('*')
    .in('automation_id', automationIds)
    .order('execution_order', { ascending: true })

  if (actionsError) {
    console.error('[automation] Error fetching automation actions:', actionsError)
    throw actionsError
  }

  const actionsByAutomation = new Map()
  for (const action of actions || []) {
    const list = actionsByAutomation.get(action.automation_id) || []
    list.push(action)
    actionsByAutomation.set(action.automation_id, list)
  }

  const result = automations.map(a => ({
    id: a.id,
    name: a.name,
    category: a.category || null,
    time_offset_minutes: a.time_offset_minutes || null,
    time_offset_type: a.time_offset_type || null,
    trigger_type: a.trigger_type,
    trigger_config: a.trigger_config || {},
    // Map status field to is_active boolean (database uses status: 'active'|'inactive')
    is_active: a.status === 'active',
    actions: (actionsByAutomation.get(a.id) || [])
  }))

  console.log('[automation] getAutomationsWithActions success, found', result.length, 'automations with actions')
  return createSuccessResponse(result)
}

/**
 * Upsert automation
 * Migrated from supabaseApi.js lines 189-229
 * @param {object} automation - Automation object (can include company_id to skip internal lookup)
 */
const _upsertAutomation = async (automation) => {
  console.log('[automation] upsertAutomation called with automation:', automation)
  
  validateRequired(automation, 'Automation')
  validateRequired(automation.name, 'Automation name')
  validateRequired(automation.trigger_type, 'Trigger type')
  
  // Use provided company_id or fetch it
  const companyId = automation.company_id || await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  console.log('[automation] Using company ID:', companyId)

  // Get current user for created_by field (required for inserts)
  const user = await getCurrentUser()
  if (!user) {
    throw new Error('User not authenticated')
  }

  // *** CHECK ACTIVE AUTOMATION COUNT RESTRICTION (only for new automations) ***
  if (!automation.id && automation.is_active) {
    const { data: checkData, error: checkError } = await supabase.rpc('check_restriction', {
      p_company_id: companyId,
      p_restriction_name: 'active_automations',
      p_increment_amount: 1,
      p_action_name: 'create_automation'
    })

    if (checkError) {
      throw new Error(`Failed to check automation restrictions: ${checkError.message}`)
    }

    const restrictionCheck = checkData[0]
    if (!restrictionCheck.allowed) {
      throw new Error(`Active automation limit exceeded: ${restrictionCheck.message}`)
    }
  }

  // Build payload - include all fields (columns exist in DB)
  const payload = {
    company_id: companyId,
    name: sanitizeInput(automation.name),
    description: automation.description ? sanitizeInput(automation.description) : null,
    trigger_type: automation.trigger_type,
    trigger_conditions: automation.trigger_config || {},
    status: automation.is_active ? 'active' : 'inactive',
    // Include new columns (they exist in DB, set to null if not provided)
    category: automation.category || null,
    time_offset_minutes: automation.time_offset_minutes ?? null,
    time_offset_type: automation.time_offset_type || null
  }

  // Only add created_by for new automations (not updates)
  if (!automation.id) {
    payload.created_by = user.id
  }

  if (automation.id) {
    // First try with select to get full data
    let { data, error } = await supabase
      .from('automations')
      .update(payload)
      .eq('id', automation.id)
      .eq('company_id', companyId)
      .select('*')
      .single()
    
    // If select fails, retry without select and fetch separately
    if (error && error.message && error.message.includes('column')) {
      console.warn('[automation] Select failed, retrying without select:', error.message)
      const { error: updateError } = await supabase
        .from('automations')
        .update(payload)
        .eq('id', automation.id)
        .eq('company_id', companyId)
      
      if (updateError) {
        console.error('[automation] Error updating automation:', updateError)
        throw updateError
      }
      
      // Fetch the updated record separately
      const { data: fetchedData, error: fetchError } = await supabase
        .from('automations')
        .select('*')
        .eq('id', automation.id)
        .single()
      
      if (fetchError) {
        console.error('[automation] Error fetching updated automation:', fetchError)
        throw fetchError
      }
      
      data = fetchedData
      error = null
    }
      
    if (error) {
      console.error('[automation] Error updating automation:', error)
      throw error
    }
    
    console.log('[automation] upsertAutomation success, updated automation:', data.id)
    return { data }
  } else {
    // First try with select to get full data
    let { data, error } = await supabase
      .from('automations')
      .insert(payload)
      .select('*')
      .single()
    
    // If select fails, retry without select and fetch separately
    if (error && error.message && (error.message.includes('column') || error.code === 'PGRST116')) {
      console.warn('[automation] Select failed, retrying without select:', error.message)
      const { data: insertData, error: insertError } = await supabase
        .from('automations')
        .insert(payload)
        .select('id')
        .single()
      
      if (insertError) {
        console.error('[automation] Error creating automation:', insertError)
        throw insertError
      }
      
      // Fetch the created record separately
      const { data: fetchedData, error: fetchError } = await supabase
        .from('automations')
        .select('*')
        .eq('id', insertData.id)
        .single()
      
      if (fetchError) {
        console.error('[automation] Error fetching created automation:', fetchError)
        throw fetchError
      }
      
      data = fetchedData
      error = null
    }
      
    if (error) {
      console.error('[automation] Error creating automation:', error)
      throw error
    }

    // *** INCREMENT ACTIVE AUTOMATION COUNT AFTER SUCCESSFUL CREATION ***
    if (automation.is_active) {
      try {
        const { error: incrementError } = await supabase.rpc('increment_usage', {
          p_company_id: companyId,
          p_restriction_name: 'active_automations',
          p_amount: 1
        })

        if (incrementError) {
          console.error('Failed to increment active automation count:', incrementError)
          // Don't fail the request, just log it
        }
      } catch (error) {
        console.error('Error incrementing active automation count:', error)
      }
    }
    
    console.log('[automation] upsertAutomation success, created automation:', data.id)
    return { data }
  }
}

/**
 * Replace automation actions
 * Migrated from supabaseApi.js lines 230-267
 */
const _replaceAutomationActions = async (automationId, actions) => {
  console.log('[automation] replaceAutomationActions called for automation:', automationId, 'with', actions?.length || 0, 'actions')
  
  validateRequired(automationId, 'Automation ID')
  validateRequired(actions, 'Actions')

  // Remove existing actions first
  const { error: delError } = await supabase
    .from('automation_actions')
    .delete()
    .eq('automation_id', automationId)
    
  if (delError) {
    console.error('[automation] Error deleting existing actions:', delError)
    throw delError
  }

  if (!actions || actions.length === 0) {
    console.log('[automation] No actions to insert, returning success')
    return { success: true }
  }

  const rows = actions.map((action, index) => ({
    automation_id: automationId,
    action_type: action.action_type,
    execution_order: index,
    subject: action.subject || null,
    body: action.body || null,
    action_config: action.action_config || {},
    delay_minutes: typeof action.delay_minutes === 'number' ? action.delay_minutes : 0,
    is_active: action.is_active !== false
  }))

  const { error: insError } = await supabase
    .from('automation_actions')
    .insert(rows)
    
  if (insError) {
    console.error('[automation] Error inserting automation actions:', insError)
    throw insError
  }

  console.log('[automation] replaceAutomationActions success, replaced', rows.length, 'actions')
  return { success: true }
}

/**
 * Delete automation
 * Migrated from supabaseApi.js lines 323-337
 */
const _deleteAutomation = async (automationId) => {
  console.log('[automation] deleteAutomation called for ID:', automationId)
  
  validateRequired(automationId, 'Automation ID')
  
  const { error } = await supabase
    .from('automations')
    .delete()
    .eq('id', automationId)
    
  if (error) {
    console.error('[automation] Error deleting automation:', error)
    throw error
  }

  console.log('[automation] deleteAutomation success for automation:', automationId)
  return { success: true }
}

// Export functions with error handling
export const getAutomationsWithActions = withErrorHandling(_getAutomationsWithActions, 'get automations with actions', 'automations')
export const upsertAutomation = withErrorHandling(_upsertAutomation, 'upsert automation', 'automation')
export const replaceAutomationActions = withErrorHandling(_replaceAutomationActions, 'replace automation actions', 'automation_actions')
export const deleteAutomation = withErrorHandling(_deleteAutomation, 'delete automation', 'automation')